import { useState, useEffect, useMemo } from 'react'
import type { Patient } from '@medplum/fhirtypes'
import { useMedplum } from '@medplum/react'
import {
  TextInput,
  Checkbox,
  Paper,
  Button,
  Group,
  Stack,
  Text,
  Table,
  Badge,
  Collapse,
  Textarea,
  ActionIcon,
  Stepper,
  Loader,
} from '@mantine/core'
import { useForm } from '@mantine/form'
import {
  IconChevronDown,
  IconChevronRight,
  IconSearch,
  IconPlus,
  IconTrash,
  IconX,
} from '@tabler/icons-react'
import { notifications } from '@mantine/notifications'
import { useCanOrder } from './shared/useCanOrder'
import { useSharedPanels } from './shared/useSharedPanels'
import { CustomPanelsSection } from './CustomPanelsSection'
import { PANEL_VENDOR_QUEST } from './customPanelStorage'
import { useAutoPopulate } from './shared/useAutoPopulate'
import { useSignature } from './shared/useSignature'
import { useOrderSubmission } from './shared/useOrderSubmission'
import { FormHeader } from './shared/FormHeader'
import { SignatureSection } from './shared/SignatureSection'
import { DiagnosisCodesSection } from './shared/DiagnosisCodesSection'
import { logWarning } from './shared/errorUtils'
import { generateBeHumanOrderPdf } from './pdf/behumanPdfGenerator'
import { mapQuestToPdf } from './pdf/mapQuestToPdf'
import { loadLogoDataUrl } from './shared/loadLogoDataUrl'
import type { AutoPopulateData } from './pdf/pdfTypes'
import { OrderFormLayout } from './shared/OrderFormLayout'
import { OrderCart } from './shared/OrderCart'
import type { CartItem } from './shared/OrderCart'
import { OrderReviewSummary } from './shared/OrderReviewSummary'
import type { ReviewSection } from './shared/OrderReviewSummary'
import { LabFacilityStep } from './LabFacilityStep'
import type { Facility } from './shared/facilityTypes'
import { useTestCatalog } from './shared/useTestCatalog'
import type { CatalogTest } from './shared/useTestCatalog'
import { useDebounce } from '@/hooks/useDebounce'
import {
  QUEST_QUESTIONNAIRE_ID,
  QUEST_MICROBIOLOGY_SOURCES,
  createInitialQuestFormState,
} from './questFormData'
import type { QuestFormState, QuestWriteInTest } from './questFormData'
import { useSaveDraftCoordinatorFlow } from './shared/useSaveDraftCoordinatorFlow'
import { draftOnlyReason, SIGNATURE_REQUIRED_TOOLTIP } from './shared/constants'
import { TooltipButton } from './shared/TooltipButton'

type Props = {
  patient: Patient
  onBack: () => void
}

type Step = 'tests' | 'facility' | 'details' | 'review'
const STEPS: Step[] = ['tests', 'facility', 'details', 'review']

/** Section ordering for favorite tests. Sections render dynamically from FHIR data. */
const QUEST_FAVORITE_SECTIONS: {
  category: string
  label: string
  description?: string
}[] = [
  {
    category: 'organ-disease-panels',
    label: 'ORGAN / DISEASE PANELS',
    description: 'Select applicable organ or disease panel tests.',
  },
  { category: 'hematology', label: 'HEMATOLOGY' },
  { category: 'thyroid', label: 'THYROID' },
  { category: 'adrenal', label: 'ADRENAL / ENDOCRINE' },
  { category: 'diabetes', label: 'DIABETES' },
  { category: 'reproductive', label: 'REPRODUCTIVE & SEXUAL MATURATION' },
  { category: 'bone-kidney', label: 'PARATHYROID, CALCIUM, BONE & KIDNEY' },
  { category: 'growth', label: 'GROWTH' },
  { category: 'microbiology', label: 'MICROBIOLOGY' },
]

export const QuestRequisitionForm = ({ patient, onBack }: Props) => {
  const [step, setStep] = useState<Step>('tests')
  const [selectedFacility, setSelectedFacility] = useState<Facility | null>(null)
  // PD-1312 — "Patient will choose a Quest Diagnostics location": no branch is
  // selected and nothing is faxed; the patient carries a printable requisition
  // valid at any Quest location. Mutually exclusive with `selectedFacility`.
  const [patientChoosesLocation, setPatientChoosesLocation] = useState(false)
  // PD-1929 — fax delivery chosen before a facility is picked. Kept here, not in the
  // delivery step, so going Back to change the tests doesn't forget it (Greptile !2060).
  const [faxDeliveryChosen, setFaxDeliveryChosen] = useState(false)

  const {
    patientDefaults,
    providerDefaults,
    insuranceDefaults,
    diagnosisDefaults,
    orgError,
    insuranceWarning,
  } = useAutoPopulate(patient)
  const medplum = useMedplum()
  const signature = useSignature()
  const { canOrder: isPractitioner, loading: canOrderLoading } = useCanOrder()

  // Test catalog — favorites (loaded once) and search (on query change)
  const { tests: allFavorites, loading: favoritesLoading } = useTestCatalog('quest', {
    favoritesOnly: true,
    _count: 5000,
  })

  const [testSearch, setTestSearch] = useState('')
  const debouncedTestSearch = useDebounce(testSearch, 300)
  const { tests: searchResults, loading: searchLoading } = useTestCatalog('quest', {
    q: debouncedTestSearch,
    _count: 50,
    enabled: debouncedTestSearch.length >= 2,
  })

  // Filter out 'general' category favorites (those are search-only per user preference)
  const favorites = useMemo(
    () => allFavorites.filter((t) => t.category !== 'general'),
    [allFavorites],
  )

  // Group favorites by category for section rendering
  const favoritesByCategory = useMemo(() => {
    const map = new Map<string, CatalogTest[]>()
    for (const test of favorites) {
      const cat = test.category || 'other'
      if (!map.has(cat)) map.set(cat, [])
      map.get(cat)!.push(test)
    }
    return map
  }, [favorites])

  // Shared, practice-wide panels — read-only, owned by the organization. Quest
  // has no personal-panel UI, so this is the only panels section on this form.
  const {
    panels: sharedPanels,
    testLookup: sharedTestLookup,
    loading: sharedPanelsLoading,
  } = useSharedPanels(PANEL_VENDOR_QUEST)

  // Test lookup map — merges favorites + search results for cart display and PDF.
  // Shared-panel tests are merged in FIRST so their names show even though the
  // Quest catalog slice loaded here is only favorites plus the current search
  // (Quest's compendium is ~4,500 tests); a live catalog hit wins on conflict.
  const testMap = useMemo(() => {
    const map = new Map<string, CatalogTest>()
    for (const [code, t] of sharedTestLookup) {
      map.set(code, {
        id: code,
        code,
        name: t.name,
        specimen: t.specimen,
        category: t.category,
        categoryDisplay: t.categoryDisplay,
      })
    }
    for (const t of allFavorites) map.set(t.code, t)
    for (const t of searchResults) map.set(t.code, t)
    return map
  }, [allFavorites, searchResults, sharedTestLookup])

  const autoPopulateData: AutoPopulateData = {
    patient: patientDefaults,
    provider: providerDefaults,
    insurance: insuranceDefaults,
  }

  const form = useForm<QuestFormState>({
    initialValues: createInitialQuestFormState(),
    validateInputOnBlur: true,
  })

  useEffect(() => {
    if (providerDefaults.organizationName) {
      form.setValues({
        organizationName: providerDefaults.organizationName,
        orgPhone: providerDefaults.orgPhone,
        orgFax: providerDefaults.orgFax,
        orgAddress: providerDefaults.orgAddress,
        orgCity: providerDefaults.orgCity,
        orgState: providerDefaults.orgState,
        orgZip: providerDefaults.orgZip,
      })
    }
  }, [providerDefaults.organizationName]) // eslint-disable-line react-hooks/exhaustive-deps

  const submission = useOrderSubmission({
    patientId: patient.id,
    questionnaireId: QUEST_QUESTIONNAIRE_ID,
    generatePdfBytes: async () => {
      const logoDataUrl = selectedFacility?.logoUrl
        ? await loadLogoDataUrl(medplum, selectedFacility.logoUrl)
        : undefined
      return generateBeHumanOrderPdf(
        mapQuestToPdf(
          form.values,
          autoPopulateData,
          selectedFacility ?? undefined,
          testMap,
          logoDataUrl,
          patientChoosesLocation,
        ),
      )
    },
    buildFileName: () =>
      `Quest_Order_${patientDefaults.lastName}_${patientDefaults.firstName}_${new Date()
        .toISOString()
        .slice(0, 10)}.pdf`,
    getFormData: () =>
      ({
        ...form.values,
        performerOrganizationId: selectedFacility?.id,
        // PD-1312 — tells middleware this requisition is patient-carried, so the
        // PDF is stamped `LabRequisition` and surfaces in the patient portal instead
        // of being hidden there as `Order`. The chart shows it under Orders only
        // (PD-1745): Documents excludes the code so the order is not listed twice.
        patientChoosesLocation,
      }) as unknown as Record<string, unknown>,
    vendorName: 'Quest Diagnostics',
    laboratoryOrganizationName: 'Quest Diagnostics',
    diagnosisDefaults,
    getDiagnosisText: () => form.values.diagnosisText,
    orgError,
    overrideFaxNumber: selectedFacility ? selectedFacility.fax || '' : null,
    // PD-1312 — nothing is transmitted when the patient picks the location, so
    // skip the fax lookup (which would otherwise block on "no fax available")
    // and the /order/send-fax call. /order/create still runs.
    skipFax: patientChoosesLocation,
  })

  // Facility draft/submit logic
  const facilityPendingApproval =
    selectedFacility && 'pendingApproval' in selectedFacility && selectedFacility.pendingApproval
  const noFacilitySelected = !selectedFacility
  // PD-1312 — a patient-chosen location has no facility by design, so none of
  // the facility gates apply and the order can be submitted outright.
  const mustSaveAsDraft =
    !patientChoosesLocation &&
    (noFacilitySelected || (selectedFacility && !selectedFacility.fax) || !!facilityPendingApproval)

  const stepIndex = STEPS.indexOf(step)
  // PD-1929 — a faxed order needs the fax number before Details, not a draft at Review.
  const canAdvanceFromFacility =
    patientChoosesLocation || (!!form.values.selectedFacilityId && !!selectedFacility?.fax)

  const handleBack = () => {
    const idx = STEPS.indexOf(step)
    if (idx === 0) {
      onBack()
    } else {
      setStep(STEPS[idx - 1])
    }
  }

  // Auto-apply saved signature
  useEffect(() => {
    if (signature.savedSignature && !form.values.signatureDataUrl) {
      form.setFieldValue('signatureDataUrl', signature.savedSignature)
      form.setFieldValue(
        'signatureDate',
        new Date().toLocaleDateString('en-US', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
        }),
      )
    }
  }, [signature.savedSignature]) // eslint-disable-line react-hooks/exhaustive-deps

  // UI state
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set())

  const toggleSection = (section: string) => {
    setExpandedSections((prev) => {
      const next = new Set(prev)
      if (next.has(section)) next.delete(section)
      else next.add(section)
      return next
    })
  }

  // Toggle a test code in the unified selectedTests array
  const toggleTest = (code: string) => {
    const current = form.values.selectedTests
    const next = current.includes(code) ? current.filter((c) => c !== code) : [...current, code]
    form.setFieldValue('selectedTests', next)
  }

  // Select/deselect a shared panel as a unit: if every one of its tests is
  // already checked, clear them; otherwise add the missing ones. Matches the
  // Labcorp behaviour so a panel's checkbox and its tests never disagree.
  const handleToggleSharedPanel = (testCodes: string[]) => {
    const current = form.values.selectedTests
    const allSelected = testCodes.length > 0 && testCodes.every((c) => current.includes(c))
    const next = allSelected
      ? current.filter((c) => !testCodes.includes(c))
      : [...current, ...testCodes.filter((c) => !current.includes(c))]
    form.setFieldValue('selectedTests', next)
  }

  // Toggle for non-test string arrays (microbiology sources)
  const toggleArrayItem = (field: keyof QuestFormState, value: string) => {
    const current = form.values[field] as unknown as string[]
    const next = current.includes(value) ? current.filter((c) => c !== value) : [...current, value]
    form.setFieldValue(field as never, next as never)
  }

  // Write-in tests
  const addWriteInTest = () => {
    form.setFieldValue('writeInTests', [
      ...form.values.writeInTests,
      { testNumber: '', testName: '' },
    ])
  }

  const updateWriteInTest = (index: number, field: keyof QuestWriteInTest, value: string) => {
    form.setFieldValue(
      'writeInTests',
      form.values.writeInTests.map((t, i) => (i === index ? { ...t, [field]: value } : t)),
    )
  }

  const removeWriteInTest = (index: number) => {
    form.setFieldValue(
      'writeInTests',
      form.values.writeInTests.filter((_, i) => i !== index),
    )
  }

  const totalSelectedCount =
    form.values.selectedTests.length +
    form.values.writeInTests.filter((t) => t.testNumber || t.testName).length

  // Cart items — grouped by category from the test map
  const cartItems: CartItem[] = useMemo(() => {
    const items: CartItem[] = []
    for (const code of form.values.selectedTests) {
      const test = testMap.get(code)
      items.push({
        id: `${test?.categoryDisplay || 'Test'}::${code}`,
        code,
        name: test?.name || code,
        category: test?.categoryDisplay || 'Test',
      })
    }
    for (const wt of form.values.writeInTests) {
      if (wt.testNumber || wt.testName) {
        items.push({
          id: `Write-In::${wt.testNumber || wt.testName}`,
          code: wt.testNumber,
          name: wt.testName || wt.testNumber,
          category: 'Write-In',
        })
      }
    }
    return items
  }, [form.values.selectedTests, form.values.writeInTests, testMap])

  const handleCartRemove = (id: string) => {
    const [category, ...rest] = id.split('::')
    const code = rest.join('::')
    if (category === 'Write-In') {
      form.setFieldValue(
        'writeInTests',
        form.values.writeInTests.filter((wt) => (wt.testNumber || wt.testName) !== code),
      )
    } else {
      form.setFieldValue(
        'selectedTests',
        form.values.selectedTests.filter((c) => c !== code),
      )
    }
  }

  const reviewSections: ReviewSection[] = useMemo(() => {
    const sections: ReviewSection[] = []
    const collection = [
      { label: 'Collection Date', value: form.values.collectionDate },
      {
        label: 'Collection Time',
        value: form.values.collectionTime
          ? `${form.values.collectionTime} ${form.values.collectionAmPm}`
          : '',
      },
      { label: 'Fasting', value: form.values.isFasting ? 'Yes' : '' },
      { label: 'Stat', value: form.values.isStat ? 'Yes' : '' },
    ]
    if (collection.some((i) => i.value)) {
      sections.push({ title: 'Collection Details', items: collection })
    }
    if (
      form.values.selectedTests.some((code) => {
        const t = testMap.get(code)
        return t?.category === 'microbiology'
      }) &&
      form.values.microbiologySources.length > 0
    ) {
      sections.push({
        title: 'Microbiology',
        items: [
          { label: 'Sources', value: form.values.microbiologySources.join(', ') },
          { label: 'Other Source', value: form.values.microbiologyOtherSource },
        ],
      })
    }
    if (form.values.clinicalComments) {
      sections.push({
        title: 'Clinical Comments',
        items: [{ label: 'Comments', value: form.values.clinicalComments }],
      })
    }
    return sections
  }, [form.values, testMap])

  const handleSignatureConfirm = (dataUrl: string, date: string) => {
    form.setFieldValue('signatureDataUrl', dataUrl)
    form.setFieldValue('signatureDate', date)
    signature.closeSignatureModal()
  }

  const handleSubmitOrder = async () => {
    const validation = form.validate()
    if (validation.hasErrors) {
      notifications.show({
        title: 'Validation Error',
        message: 'Please fix the highlighted fields before submitting.',
        color: 'red',
      })
      return
    }
    if (!form.values.signatureDataUrl) {
      notifications.show({
        title: 'Signature Required',
        message: 'Please sign the form before submitting.',
        color: 'red',
      })
      return
    }

    if (totalSelectedCount === 0) {
      notifications.show({
        title: 'No Tests Selected',
        message: 'Please select at least one test before submitting.',
        color: 'red',
      })
      return
    }

    if (mustSaveAsDraft) {
      const reason = facilityPendingApproval ? 'facility pending approval' : 'facility has no fax'
      logWarning('Cannot submit order', `Practitioner blocked: ${reason}`)
      notifications.show({
        title: 'Cannot Submit',
        message: facilityPendingApproval
          ? 'This facility is pending admin approval. Use "Save as Draft" instead.'
          : 'The selected facility has no fax number on file. Use "Save as Draft" instead.',
        color: 'red',
      })
      return
    }
    await submission.handleSubmitOrder()
  }

  const coordinatorFlow = useSaveDraftCoordinatorFlow({
    patient,
    orderType: 'Quest',
    skipFacility: false,
    coordinatorNote: '',
    alreadySaved: submission.orderSubmitted,
    submitting: submission.submitting,
    onSaveDraft: submission.handleSaveDraft,
  })

  const handleSaveDraft = async () => {
    const validation = form.validate()
    if (validation.hasErrors) {
      notifications.show({
        title: 'Validation Error',
        message: 'Please fix the highlighted fields before saving.',
        color: 'red',
      })
      return
    }
    if (!form.values.signatureDataUrl) {
      notifications.show({
        title: 'Signature Required',
        message: 'Please sign the form before saving.',
        color: 'red',
      })
      return
    }
    await coordinatorFlow.triggerSaveDraft()
  }

  const handleReviewOrder = () => {
    if (totalSelectedCount === 0) {
      notifications.show({
        title: 'No Tests Selected',
        message: 'Please select at least one test before reviewing.',
        color: 'red',
      })
      return
    }
    setStep('review')
  }

  // Reusable test table renderer
  const renderTestTable = (tests: CatalogTest[]) => (
    <Table withTableBorder withColumnBorders>
      <Table.Thead>
        <Table.Tr>
          <Table.Th w={40} />
          <Table.Th w={80}>Code</Table.Th>
          <Table.Th>Test Name</Table.Th>
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        {tests.map((test) => (
          <Table.Tr key={test.code}>
            <Table.Td>
              <Checkbox
                checked={form.values.selectedTests.includes(test.code)}
                onChange={() => toggleTest(test.code)}
              />
            </Table.Td>
            <Table.Td>
              <Text size="xs" className="font-mono">
                {test.code}
              </Text>
            </Table.Td>
            <Table.Td>
              <Text size="xs" fw={500}>
                {test.name}
              </Text>
            </Table.Td>
          </Table.Tr>
        ))}
      </Table.Tbody>
    </Table>
  )

  // Collapsible section header
  const renderSectionToggle = (label: string, count: number) => (
    <Button
      variant="subtle"
      size="sm"
      fullWidth
      justify="space-between"
      rightSection={
        expandedSections.has(label) ? <IconChevronDown size={16} /> : <IconChevronRight size={16} />
      }
      onClick={() => toggleSection(label)}
      className="!text-left"
    >
      <Group gap="xs">
        <Text fw={600} size="sm">
          {label}
        </Text>
        <Badge size="xs" variant="light">
          {count} tests
        </Badge>
      </Group>
    </Button>
  )

  return (
    <OrderFormLayout
      cart={
        <OrderCart items={cartItems} onRemove={handleCartRemove} vendorLabel="Quest Diagnostics" />
      }
      header={
        <>
          <FormHeader
            title="Quest Diagnostics General Requisition"
            subtitle="Select how the patient will use the lab order"
            onBack={handleBack}
            onPreview={submission.handlePreviewPdf}
            onSubmit={handleSubmitOrder}
            exporting={submission.exporting}
            submitting={submission.submitting}
            orderSubmitted={submission.orderSubmitted}
            canSubmit={isPractitioner && step === 'review' && !mustSaveAsDraft}
            canOrderLoading={canOrderLoading}
            hideActions
            submitTooltip={
              !isPractitioner
                ? 'Practitioner permissions required to submit orders'
                : step !== 'review'
                  ? 'Complete all steps to submit'
                  : undefined
            }
            labError={submission.labError}
            orgError={orgError}
            warnings={insuranceWarning ? [insuranceWarning] : []}
          />

          <Stepper active={stepIndex} mb="lg" size="sm">
            <Stepper.Step label="Tests" description="Select lab tests" />
            <Stepper.Step label="Order delivery" description="Patient or fax" />
            <Stepper.Step label="Details" description="Clinical info & signature" />
            <Stepper.Step label="Review" description="Review & submit" />
          </Stepper>
        </>
      }
    >
      {/* ===== STEP 1: TESTS ===== */}
      {step === 'tests' && (
        <Stack gap="lg">
          {/* ===== PRACTICE PANELS (shared, read-only) ===== */}
          <Paper p="md" withBorder>
            <Text fw={600} size="lg" mb="sm" className="text-[#6B21A8]">
              PRACTICE PANELS
            </Text>
            <Text size="xs" c="dimmed" mb="sm">
              Shared test groups set up for your practice. Selecting a panel auto-checks its
              individual tests.
            </Text>
            <CustomPanelsSection
              readOnly
              panels={sharedPanels}
              selectedTests={form.values.selectedTests}
              testLookup={testMap}
              onTogglePanel={handleToggleSharedPanel}
              onCreatePanel={() => {}}
              onEditPanel={() => {}}
              onDeletePanel={() => {}}
              loading={sharedPanelsLoading}
            />
          </Paper>

          {favoritesLoading && (
            <Group justify="center" py="xl">
              <Loader size="sm" />
              <Text size="sm" c="dimmed">
                Loading test catalog...
              </Text>
            </Group>
          )}

          {/* Dynamic favorite sections from FHIR */}
          {!favoritesLoading &&
            QUEST_FAVORITE_SECTIONS.map((section) => {
              const sectionTests = favoritesByCategory.get(section.category) || []
              if (sectionTests.length === 0) return null

              return (
                <Paper p="md" withBorder key={section.category}>
                  <Text fw={600} size="lg" mb="sm" className="text-[#6B21A8]">
                    {section.label}
                  </Text>
                  {section.description && (
                    <Text size="xs" c="dimmed" mb="sm">
                      {section.description}
                    </Text>
                  )}
                  {renderSectionToggle(section.label, sectionTests.length)}
                  <Collapse in={expandedSections.has(section.label)}>
                    <div className="mr-4 ml-4">
                      {/* Microbiology source checkboxes */}
                      {section.category === 'microbiology' && (
                        <>
                          <Text size="xs" fw={600} mb="xs" mt="sm">
                            Source:
                          </Text>
                          <Group gap="md" mb="sm">
                            {QUEST_MICROBIOLOGY_SOURCES.map((source) => (
                              <Checkbox
                                key={source}
                                label={source}
                                checked={form.values.microbiologySources.includes(source)}
                                onChange={() => toggleArrayItem('microbiologySources', source)}
                              />
                            ))}
                            <TextInput
                              placeholder="Other source..."
                              value={form.values.microbiologyOtherSource}
                              onChange={(e) =>
                                form.setFieldValue('microbiologyOtherSource', e.target.value)
                              }
                              size="xs"
                              w={160}
                            />
                          </Group>
                        </>
                      )}
                      {renderTestTable(sectionTests)}
                    </div>
                  </Collapse>
                </Paper>
              )
            })}

          {/* ===== SEARCH ALL TESTS ===== */}
          <Paper p="md" withBorder>
            <Text fw={600} size="lg" mb="sm" className="text-[#6B21A8]">
              SEARCH ALL TESTS
            </Text>
            <Text size="xs" c="dimmed" mb="sm">
              Search the full Quest Diagnostics compendium — 4,500+ tests available.
            </Text>
            <TextInput
              placeholder="Search by test name or code (e.g. CBC, lipid, 10231)..."
              leftSection={<IconSearch size={16} />}
              value={testSearch}
              onChange={(e) => setTestSearch(e.target.value)}
              mb="sm"
              rightSection={
                testSearch ? (
                  <ActionIcon
                    variant="subtle"
                    color="gray"
                    size="sm"
                    aria-label="Clear search"
                    onClick={() => setTestSearch('')}
                  >
                    <IconX size={14} />
                  </ActionIcon>
                ) : null
              }
              rightSectionPointerEvents={testSearch ? 'all' : 'none'}
            />

            {testSearch.trim().length < 2 && (
              <Text size="sm" c="dimmed" ta="center" py="sm">
                Type at least 2 characters to search.
              </Text>
            )}

            {searchLoading && debouncedTestSearch.length >= 2 && (
              <Group justify="center" py="sm">
                <Loader size="xs" />
                <Text size="sm" c="dimmed">
                  Searching...
                </Text>
              </Group>
            )}

            {!searchLoading && searchResults.length > 0 && (
              <>
                {renderTestTable(searchResults)}
                {searchResults.length >= 50 && (
                  <Text size="xs" c="dimmed" ta="center" mt="xs">
                    Showing first 50 matches. Refine your search for more specific results.
                  </Text>
                )}
              </>
            )}

            {!searchLoading && debouncedTestSearch.length >= 2 && searchResults.length === 0 && (
              <Text size="sm" c="dimmed" ta="center" py="sm">
                No tests found matching &quot;{testSearch}&quot;.
              </Text>
            )}
          </Paper>

          {/* ===== OTHER TESTS (WRITE-IN) ===== */}
          <Paper p="md" withBorder>
            <Group justify="space-between" mb="sm">
              <Text fw={600} size="lg" className="text-[#6B21A8]">
                OTHER TESTS (WRITE-IN)
              </Text>
              <Button
                variant="light"
                size="xs"
                leftSection={<IconPlus size={14} />}
                onClick={addWriteInTest}
              >
                Add Test
              </Button>
            </Group>
            <Text size="xs" c="dimmed" mb="sm">
              Enter test numbers and names not listed above.
            </Text>
            {form.values.writeInTests.length === 0 && (
              <Text size="sm" c="dimmed" ta="center" py="sm">
                No write-in tests added. Click &quot;Add Test&quot; to add one.
              </Text>
            )}
            <Stack gap="xs">
              {form.values.writeInTests.map((test, idx) => (
                <Group key={idx} gap="sm" wrap="nowrap">
                  <TextInput
                    placeholder="Test #"
                    value={test.testNumber}
                    onChange={(e) => updateWriteInTest(idx, 'testNumber', e.target.value)}
                    w={120}
                  />
                  <TextInput
                    placeholder="Test Name"
                    value={test.testName}
                    onChange={(e) => updateWriteInTest(idx, 'testName', e.target.value)}
                    className="flex-1"
                  />
                  <ActionIcon variant="subtle" color="red" onClick={() => removeWriteInTest(idx)}>
                    <IconTrash size={16} />
                  </ActionIcon>
                </Group>
              ))}
            </Stack>
          </Paper>

          {/* Step 1 navigation */}
          <Group justify="flex-end">
            <Button variant="default" onClick={onBack}>
              Cancel
            </Button>
            <Button
              color="teal"
              onClick={() => setStep('facility')}
              disabled={totalSelectedCount === 0}
            >
              Next: Order Delivery
            </Button>
          </Group>
        </Stack>
      )}

      {/* ===== STEP 2: FACILITY ===== */}
      {step === 'facility' && (
        <LabFacilityStep
          patient={patient}
          vendor="quest"
          selectedFacilityId={form.values.selectedFacilityId}
          onSelectFacility={(id, facility) => {
            // Picking a branch is the other half of the either/or.
            setPatientChoosesLocation(false)
            form.setFieldValue('selectedFacilityId', id)
            if (facility) setSelectedFacility(facility)
          }}
          patientChoosesLocation={patientChoosesLocation}
          onPatientChoosesLocation={() => {
            setPatientChoosesLocation(true)
            setFaxDeliveryChosen(false)
            form.setFieldValue('selectedFacilityId', '')
            setSelectedFacility(null)
          }}
          onFaxDelivery={() => {
            setPatientChoosesLocation(false)
            setFaxDeliveryChosen(true)
          }}
          faxDeliveryChosen={faxDeliveryChosen}
          selectedFacilityFax={selectedFacility?.fax || undefined}
          canAdvance={canAdvanceFromFacility}
          onBack={() => setStep('tests')}
          onNext={() => setStep('details')}
        />
      )}

      {/* ===== STEP 3: DETAILS ===== */}
      {step === 'details' && (
        <Stack gap="lg">
          {/* ===== DIAGNOSIS / ICD CODES ===== */}
          <DiagnosisCodesSection
            value={form.values.diagnosisText}
            defaultCodes={diagnosisDefaults}
            onChange={(v) => form.setFieldValue('diagnosisText', v)}
          />

          {/* ===== CLINICAL INFORMATION / COMMENTS ===== */}
          <Paper p="md" withBorder>
            <Text fw={600} size="lg" mb="sm" className="text-[#6B21A8]">
              CLINICAL INFORMATION / COMMENTS
            </Text>
            <Textarea
              placeholder="Enter any additional clinical information or comments..."
              value={form.values.clinicalComments}
              onChange={(e) => form.setFieldValue('clinicalComments', e.target.value)}
              rows={3}
            />
          </Paper>

          {/* ===== SIGNATURE ===== */}
          <SignatureSection
            signatureDataUrl={form.values.signatureDataUrl}
            signatureDate={form.values.signatureDate}
            savedSignature={signature.savedSignature}
            signatureModalOpen={signature.signatureModalOpen}
            onOpenModal={signature.openSignatureModal}
            onCloseModal={signature.closeSignatureModal}
            onConfirm={handleSignatureConfirm}
            onSaveSignature={signature.handleSaveSignature}
            certificationText="Please order only if medically necessary and appropriate for individual patients."
          />

          <Group justify="space-between">
            <Button variant="default" onClick={() => setStep('facility')}>
              Back
            </Button>
            <TooltipButton
              color="teal"
              disabled={submission.orderSubmitted || !form.values.signatureDataUrl}
              disabledReason={!form.values.signatureDataUrl ? SIGNATURE_REQUIRED_TOOLTIP : null}
              onClick={handleReviewOrder}
            >
              Review Order
            </TooltipButton>
          </Group>
        </Stack>
      )}

      {/* ===== STEP 4: REVIEW ===== */}
      {step === 'review' && (
        <Stack gap="lg">
          <Paper p="md" withBorder>
            <Text fw={600} size="lg" mb="md">
              ORDER SUMMARY
            </Text>

            {/* Selected facility */}
            <Text fw={500} size="sm" mb="xs">
              Facility
            </Text>
            {patientChoosesLocation ? (
              <div className="mb-4 rounded-md border border-blue-200 bg-blue-50 p-3">
                <Text fw={600} size="sm" c="blue">
                  Patient will choose a Quest Diagnostics location
                </Text>
                <Text size="xs" mt={4}>
                  No facility fax is required. The patient receives a printable requisition valid at
                  any Quest Diagnostics location offering the ordered test.
                </Text>
              </div>
            ) : selectedFacility ? (
              <div className="mb-4 rounded-md border border-gray-200 p-3">
                <Text fw={600} size="sm">
                  {selectedFacility.name}
                </Text>
                <Text size="xs" c="dimmed">
                  {selectedFacility.address}, {selectedFacility.city}, {selectedFacility.state}{' '}
                  {selectedFacility.zip}
                </Text>
                {selectedFacility.fax && (
                  <Text size="xs" c="dimmed">
                    Fax: {selectedFacility.fax}
                  </Text>
                )}
              </div>
            ) : null}
          </Paper>

          <OrderReviewSummary
            vendorName="Quest Diagnostics"
            tests={cartItems}
            diagnosisText={form.values.diagnosisText}
            sections={reviewSections}
            signatureDataUrl={form.values.signatureDataUrl}
            signatureDate={form.values.signatureDate}
            submitLabel={patientChoosesLocation ? 'Submit & Save Requisition' : undefined}
            submittedLabel={patientChoosesLocation ? 'Requisition Saved' : undefined}
            onEdit={() => setStep('details')}
            onSubmit={handleSubmitOrder}
            onPreview={submission.handlePreviewPdf}
            submitting={submission.submitting}
            exporting={submission.exporting}
            orderSubmitted={submission.orderSubmitted}
            isPractitioner={isPractitioner}
            canOrderLoading={canOrderLoading}
            labError={submission.labError}
            orgError={orgError}
            mustSaveAsDraft={!!mustSaveAsDraft}
            draftNotice={draftOnlyReason({
              usingCoordinatorNote: false,
              facilityPendingApproval: !!facilityPendingApproval,
            })}
            onSaveDraft={handleSaveDraft}
          />
        </Stack>
      )}
      {coordinatorFlow.modal}
    </OrderFormLayout>
  )
}
